import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import GoogleTagTracker from "./components/GoogleTagTracker";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

// Pages
import Home from "./pages/Home";
import About from "./pages/About";
import Energiramme from "./pages/Energiramme";
import Energimaerkning from "./pages/Energimaerkning";
import Termografi from "./pages/Termografi";
import TermografiKomfortundersoegelse from "./pages/TermografiKomfortundersoegelse";
import Trykprovning from "./pages/Trykprovning";
import Prices from "./pages/Prices";
import Prisberegner from "./pages/Prisberegner";
import Gallery from "./pages/Gallery";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import Forbidden from "./pages/Forbidden";
import CityEnergikonsulent from "./pages/CityEnergikonsulent";
import CityEnergimaerkning from "./pages/CityEnergimaerkning";
import CityTrykprovning from "./pages/CityTrykprovning";
import LCABeregning from "./pages/LCABeregning";
import CityLCA from "./pages/CityLCA";
import BlogPostLCA from "./pages/BlogPostLCA";
import Fugtmaaling from "./pages/Fugtmaaling";
import CityFugtmaaling from "./pages/CityFugtmaaling";
import BlogPostFugtmaaling from "./pages/BlogPostFugtmaaling";
import { CITIES } from "./data/cities";

function RemoveTrailingSlash() {
  const location = useLocation();
  if (location.pathname.length > 1 && location.pathname.endsWith("/")) {
    return (
      <Navigate
        to={{
          ...location,
          pathname: location.pathname.replace(/\/+$/, ""),
        }}
        replace
      />
    );
  }
  return null;
}

export default function App() {
  return (
    <div className="min-h-screen flex flex-col bg-[#FAF9F6] text-stone-800 font-sans selection:bg-[#165B4C]/20 selection:text-[#165B4C]">
      <RemoveTrailingSlash />
      <GoogleTagTracker />
      <ScrollToTop />
      <Navbar />

      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/om-os" element={<About />} />
          <Route path="/energiramme" element={<Energiramme />} />
          <Route path="/lca-beregning" element={<LCABeregning />} />
          <Route path="/lca" element={<Navigate to="/lca-beregning" replace />} />
          <Route path="/lca-beregning-i-aarhus" element={<CityLCA cityKey="aarhus" />} />
          <Route path="/lca-beregning-i-horsens" element={<CityLCA cityKey="horsens" />} />
          <Route path="/lca-beregning-i-vejle" element={<CityLCA cityKey="vejle" />} />
          <Route path="/lca-beregning-i-odense" element={<CityLCA cityKey="odense" />} />
          <Route path="/blog/lca-krav-br18-2025" element={<BlogPostLCA />} />
          <Route path="/lca-krav-br18-2025" element={<Navigate to="/blog/lca-krav-br18-2025" replace />} />
          <Route path="/energimaerkning" element={<Energimaerkning />} />
          <Route path="/termografi" element={<Termografi />} />
          <Route path="/fugtmaaling" element={<Fugtmaaling />} />
          <Route path="/fugtmaaling-i-horsens" element={<CityFugtmaaling cityKey="horsens" />} />
          <Route path="/fugtmaaling-i-aarhus" element={<CityFugtmaaling cityKey="aarhus" />} />
          <Route path="/fugtmaaling-i-odense" element={<CityFugtmaaling cityKey="odense" />} />
          <Route path="/blog/fugtmaaling-hvad-kan-den-vise" element={<BlogPostFugtmaaling />} />
          <Route path="/fugtmaaling-hvad-kan-den-vise" element={<Navigate to="/blog/fugtmaaling-hvad-kan-den-vise" replace />} />
          <Route path="/termografi-komfortundersoegelse" element={<TermografiKomfortundersoegelse />} />
          <Route path="/termografi-og-komfortundersoegelse" element={<Navigate to="/termografi-komfortundersoegelse" replace />} />
          <Route path="/komfortundersoegelse" element={<Navigate to="/termografi-komfortundersoegelse" replace />} />
          <Route path="/trykproevning" element={<Trykprovning />} />
          {/* Alias for alternative spelling */}
          <Route path="/trykprovning" element={<Navigate to="/trykproevning" replace />} />

          {/* By-specifikke landingssider (uden menupunkter) */}
          {CITIES.map((city) => (
            <React.Fragment key={city.slug}>
              <Route
                path={`/energikonsulent-i-${city.slug}`}
                element={<CityEnergikonsulent cityProp={city} />}
              />
              <Route
                path={`/energimaerkning-i-${city.slug}`}
                element={<CityEnergimaerkning cityProp={city} />}
              />
              <Route
                path={`/trykproevning-i-${city.slug}`}
                element={<CityTrykprovning cityProp={city} />}
              />
              <Route
                path={`/trykprovning-i-${city.slug}`}
                element={<CityTrykprovning cityProp={city} />}
              />
            </React.Fragment>
          ))}

          <Route path="/priser" element={<Prices />} />
          <Route path="/prisberegner" element={<Prisberegner />} />
          {/* Gamle galleri-adresser redirects til de nye dedikerede undersider */}
          <Route path="/galleri/termografi" element={<Navigate to="/termografi" replace />} />
          <Route path="/galleri-termografi" element={<Navigate to="/termografi" replace />} />
          <Route path="/galleri/trykproevning" element={<Navigate to="/trykproevning" replace />} />
          <Route path="/galleri/trykprovning" element={<Navigate to="/trykproevning" replace />} />
          <Route path="/galleri-trykproevning" element={<Navigate to="/trykproevning" replace />} />
          <Route path="/galleri/energiramme" element={<Navigate to="/energiramme" replace />} />
          <Route path="/galleri-energiramme" element={<Navigate to="/energiramme" replace />} />
          <Route path="/galleri" element={<Gallery />} />
          <Route path="/galleri/:category" element={<Gallery />} />
          <Route path="/kontakt" element={<Contact />} />
          <Route path="/403" element={<Forbidden />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <Footer />
    </div>
  );
}
